import {
  createResource,
  createResourcePath,
  deleteResource,
  getResource,
  postgRESTApiUrl,
  updateResource,
  updateResourcePath,
} from "./api";

const createByContactOwner = async (contactOwnerId, contactOwnerType, attrParam) => {
  const url = createResourcePath("contact_infos");
  const attributes = attrParam;
  attributes.contact_owner_id = contactOwnerId;
  attributes.contact_owner_type = contactOwnerType;
  return createResource(url, { data: { attributes } });
};

// The raw view exposes `id` and `value_type_string`, while every renderer of contact details expects
// the `contact_info_id`/`type` shape the profile, company and outlet aggregates build. Normalize
// here so a card and an edit surface reading the same rows agree on one shape. `id` is kept too:
// duplicate types are allowed, so an editor can only key and mutate rows by the row id.
const normalizeRow = (row) => ({
  contact_info_id: row.id,
  id: row.id,
  type: row.value_type_string,
  value: row.value,
});

// PostgREST caps how many rows one request may return, so "every row" has to be walked rather than
// asked for in one go: id_contact_infos carries no uniqueness constraint, so an owner legitimately
// holds several rows of the same type and stopping at the server's cap would silently drop real
// contact details off the end of the card. id.asc keeps both the order and the page boundaries
// stable, across reads and across an edit round trip.
const ROWS_PER_REQUEST = 1000;

const getAllByContactOwner = async (contactOwnerId, contactOwnerType) => {
  const url =
    `${postgRESTApiUrl()}/rest/v1/contact_infos?contact_owner_id=eq.${contactOwnerId}` +
    `&contact_owner_type=eq.${contactOwnerType}&order=id.asc`;

  const rows = [];
  let offset = 0;
  let response;

  do {
    // Sequential by necessity: each request's offset is the count of rows already in hand.
    // eslint-disable-next-line no-await-in-loop
    response = await getResource(`${url}&limit=${ROWS_PER_REQUEST}&offset=${offset}`);

    // A failure part-way through is returned as the failure, not as the rows collected so far: a
    // short list presented as the whole list is worse than a caller that knows the read failed.
    if (!Array.isArray(response.data)) return response;

    rows.push(...response.data);
    offset += response.data.length;
    // An empty page ends the walk whatever the count says: the row count is an estimate, so it can
    // claim rows that are not there.
  } while (response.remainingRows > 0 && response.data.length > 0);

  return { ...response, data: rows.map(normalizeRow) };
};

const updateById = async (id, attributes) => {
  const url = updateResourcePath("contact_infos", id);
  return updateResource(url, { data: { attributes } });
};

const deleteById = async (id) => {
  const url = updateResourcePath("contact_infos", id);
  return deleteResource(url);
};

export default { createByContactOwner, getAllByContactOwner, updateById, deleteById };
